import React, { useState } from 'react';
import { FaTimes, FaDollarSign, FaRupeeSign, FaCalendarAlt, FaCheckCircle, FaGlobe, FaMapMarkerAlt, FaFileInvoice } from 'react-icons/fa';

const PaymentDetailsModal = ({ isOpen, onClose, onSubmit, onSkip, userName, isLoading }) => {
  const [formData, setFormData] = useState({
    service: 'CFO Advisory',
    dateTime: new Date().toISOString().slice(0, 16),
    amount: '',
    amountUSD: '',
    currency: 'USD',
    location: 'international',
    orderId: '',
    status: 'completed',
    sendEmail: true
  });

  const services = [
    'USA Company Formation',
    'Tax and Form Return',
    'Tax and Form Return (Starting)',
    'Pay-As-You-Go',
    'Bookkeeping',
    'CFO Advisory',
    'Registered Agent',
    'Annual Report Service',
    'Direct Payment',
    'Tax + Bookkeeping Bundle',
    'Tax Filing Renewal'
  ];

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLoading) return;
    onSubmit(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (isLoading) return;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-0 md:p-4 bg-black/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="bg-white dark:bg-dark-800 md:rounded-3xl rounded-none shadow-2xl w-full max-w-lg my-auto overflow-hidden border border-gray-100 dark:border-dark-700 animate-scale-in flex flex-col max-h-screen md:max-h-[90vh]">
        <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex justify-between items-center bg-gray-50 dark:bg-dark-700/30">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <FaFileInvoice className="text-primary-600" /> Payment History Details
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Associate this invoice with a payment for <span className="text-primary-600 font-medium">{userName}</span></p>
          </div>
          <button 
            onClick={onClose} 
            disabled={isLoading}
            className="p-2 hover:bg-gray-200 dark:hover:bg-dark-600 rounded-xl transition-colors disabled:opacity-50"
          >
            <FaTimes className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Service Name</label>
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                disabled={isLoading}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all dark:text-white disabled:opacity-70"
                required
              >
                {services.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Date & Time</label>
              <div className="relative">
                <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="datetime-local"
                  name="dateTime"
                  value={formData.dateTime}
                  onChange={handleChange}
                  disabled={isLoading}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all dark:text-white disabled:opacity-70"
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Paid Amount ({formData.currency})</label>
              <div className="relative">
                {formData.currency === 'INR' ? 
                  <FaRupeeSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /> : 
                  <FaDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                }
                <input
                  type="number"
                  name="amount"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={handleChange}
                  disabled={isLoading}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all dark:text-white font-semibold disabled:opacity-70"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Currency</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => setFormData(p => ({ ...p, currency: 'USD' }))}
                  className={`flex-1 py-2.5 rounded-xl border font-bold transition-all disabled:opacity-70 ${formData.currency === 'USD' ? 'bg-primary-600 border-primary-600 text-white shadow-lg' : 'bg-gray-50 dark:bg-dark-700 border-gray-200 dark:border-dark-600 text-gray-500'}`}
                >
                  USD
                </button>
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => setFormData(p => ({ ...p, currency: 'INR' }))}
                  className={`flex-1 py-2.5 rounded-xl border font-bold transition-all disabled:opacity-70 ${formData.currency === 'INR' ? 'bg-primary-600 border-primary-600 text-white shadow-lg' : 'bg-gray-50 dark:bg-dark-700 border-gray-200 dark:border-dark-600 text-gray-500'}`}
                >
                  INR
                </button>
              </div>
            </div>
          </div>

          {formData.currency === 'INR' && formData.location === 'international' && (
            <div className="space-y-1.5 animate-fade-in">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Amount in USD (Bookkeeping)</label>
              <div className="relative">
                <FaDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  name="amountUSD"
                  placeholder="0.00"
                  value={formData.amountUSD}
                  onChange={handleChange}
                  disabled={isLoading}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all dark:text-white disabled:opacity-70"
                  required={formData.currency === 'INR' && formData.location === 'international'}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Location</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => setFormData(p => ({ ...p, location: 'india' }))}
                  className={`flex-1 py-2 flex items-center justify-center gap-2 rounded-xl border text-sm font-medium transition-all disabled:opacity-70 ${formData.location === 'india' ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-500 text-primary-600 shadow-sm' : 'bg-gray-50 dark:bg-dark-700 border-gray-200 dark:border-dark-600 text-gray-500'}`}
                >
                  <FaMapMarkerAlt /> India
                </button>
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => setFormData(p => ({ ...p, location: 'international' }))}
                  className={`flex-1 py-2 flex items-center justify-center gap-2 rounded-xl border text-sm font-medium transition-all disabled:opacity-70 ${formData.location === 'international' ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-500 text-primary-600 shadow-sm' : 'bg-gray-50 dark:bg-dark-700 border-gray-200 dark:border-dark-600 text-gray-500'}`}
                >
                  <FaGlobe /> International
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={isLoading}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all dark:text-white disabled:opacity-70"
              >
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Order ID / Payment ID (Manual)</label>
            <input
              type="text"
              name="orderId"
              placeholder="e.g. MAN-2024-001"
              value={formData.orderId}
              onChange={handleChange}
              disabled={isLoading}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all dark:text-white disabled:opacity-70"
            />
          </div>

          <div className="flex items-center gap-3 p-3 bg-primary-50/50 dark:bg-primary-900/10 rounded-2xl border border-primary-100/50 dark:border-primary-800/50">
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer"
                checked={formData.sendEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, sendEmail: e.target.checked }))}
                disabled={isLoading}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-dark-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Send Email Notification to Client</span>
          </div>

          <div className="pt-4 flex flex-col gap-3">
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-2xl shadow-lg shadow-primary-500/30 transition-all active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <FaCheckCircle /> Add to History & Send Invoice
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => onSkip(formData.sendEmail)}
              disabled={isLoading}
              className="w-full py-3 bg-gray-200 dark:bg-dark-700 hover:bg-gray-300 dark:hover:bg-dark-600 text-gray-700 dark:text-gray-300 font-semibold rounded-2xl transition-all disabled:opacity-50"
            >
              Only Send Invoice (Skip Details)
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentDetailsModal;
